import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';
import { AskingDetailsModalComponent } from '../asking-details-modal/asking-details-modal.component';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/models/user';
import { Observable, catchError, map, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../shared/auth/auth.service';
import { LoaderService } from 'src/app/services/loader.service';
import { LoginResponses } from '../../models/login-responses';
import { Router } from '@angular/router';

type SignupScreen = 'role' | 'owner' | 'staff' | 'register';

@Component({
  selector: 'app-signup',
  templateUrl: './signup-modal.component.html',
  styleUrls: ['./signup-modal.component.scss'],
})
export class SignupModalComponent {
  // #region INPUT, OUTPUT, VIEWCHILD
  @ViewChild('signUpModal') signupTemplate!: TemplateRef<SignupModalComponent>;
  @ViewChild('askingDetailsModal')
  askingDetailsComponent!: AskingDetailsModalComponent;
  @Output() signUpClickEvent = new EventEmitter<any>();
  returnURL: string = '';
  @Output() onCloseBtnClick = new EventEmitter<any>();
  // #endregion

  private modalService = inject(NgbModal);
  constructor(
    private authService: AuthService,
    private loaderService: LoaderService,
    private userService: UserService,
    private router: Router,
    private toastr: ToastrService
  ) {
    this.returnURL = this.authService.getReturnURL();
    this.authService.returnUrl$.subscribe((value: string) => {
      this.returnURL = value;
    });
}

  item: User = new User();
  isDisableSignUpBtn = false;
  emailExists: boolean = false;
  currentScreen: SignupScreen = 'role';

  formGroup = new FormGroup({
    FirstName: new FormControl("", [Validators.required, Validators.maxLength(25)]),
    LastName: new FormControl("", [Validators.required, Validators.maxLength(25)]),
    Email: new FormControl("", [Validators.required, CustomValidators.emailValidator(), Validators.maxLength(50)]),
    Password: new FormControl("", [Validators.required, CustomValidators.passwordValidator(), Validators.minLength(6), Validators.maxLength(25)]),
    RepeatPassword: new FormControl("", [Validators.required, CustomValidators.passwordValidator(), Validators.minLength(6), Validators.maxLength(25)]),
  },
    { validators: [CustomValidators.confirmPasswordValidator("Password", "RepeatPassword"), CustomValidators.onlyAlphabetsValidator("FirstName"), CustomValidators.onlyAlphabetsValidator("LastName")] }
  );

  openModal(modal: TemplateRef<any>) {
    this.modalService.open(modal, {
      windowClass: 'project-modal',
      centered: true,
      backdropClass: 'primary-backdrop',
    });
  }

  showRoleSelection() {
    this.currentScreen = 'role';
  }

  showOwnerGuide() {
    this.currentScreen = 'owner';
  }

  showStaffGuide() {
    this.currentScreen = 'staff';
  }

  showRegistrationForm() {
    this.currentScreen = 'register';
  }

  prepareRegistrationFlow() {
    this.isDisableSignUpBtn = false;
    this.currentScreen = 'role';
    this.formGroup.reset();
    this.formGroup.updateValueAndValidity();
    this.item = new User();
  }

  onSignUpClick() {
    this.isDisableSignUpBtn = true;
    this.item.firstName = this.formGroup.get('FirstName').value;
    this.item.lastName = this.formGroup.get('LastName').value;
    this.item.email = this.formGroup.get('Email').value;
    this.item.password = this.formGroup.get('Password').value;
    this.item.confirmPassword = this.formGroup.get('RepeatPassword').value;
    this.userService.updateUser(this.item);
    this.resetForm();
    this.signUpClickEvent.emit();
    this.modalService.dismissAll();
    this.openModal(this.askingDetailsComponent.askingDetailsTemplate);
  }

  onTextValueChange(field) {
    this.fromGroupTouched();
    this.item[field.text] = field.value;
  }

  fromGroupTouched(isSubmit: boolean = false) {
    if (this.formGroup.touched || isSubmit) {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }

  resetForm() {
    this.prepareRegistrationFlow();
    this.onCloseBtnClick.emit();
  }

  CheckEmail() {
    this.fromGroupTouched(true);
    if (this.formGroup.invalid) {
      return;
    }
    else {
      this.isEmailExist(this.formGroup.get('Email').value).subscribe((exists: boolean) => {
        if (exists) {
          const control = this.formGroup.controls['Email'];
          control.setErrors({ CustomError: true });
        } else {
          this.onSignUpClick();
        }
      });
    }
  }

  isEmailExist(email: string): Observable<boolean> {
    return this.userService.getById(`api/isuservalid?email=${email}`).pipe(
      map((response: any) => {
        if (response && response != 0) {
          return true;
        } else {
          return false;
        }
      }),
      catchError((error) => {
        return of(false);
      })
    );
  }

  async loginWithGoogle() {
    if (!(await this.isRegistrationAllowed())) {
      return;
    }
    const credential = await this.authService.loginWithGoogle();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  async loginWithFacebook() {
    if (!(await this.isRegistrationAllowed())) {
      return;
    }
    const credential = await this.authService.loginWithFacebook();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  async loginWithApple() {
    if (!(await this.isRegistrationAllowed())) {
      return;
    }
    const credential = await this.authService.loginWithApple();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  async loginWithInstagram() {
    if (!(await this.isRegistrationAllowed())) {
      return;
    }
    this.authService.loginWithInstagram();
  }

  private async isRegistrationAllowed(): Promise<boolean> {
    const isAllowedCountry = await this.userService.getUserCountry();
    if (!isAllowedCountry) {
      this.toastr.info("We're sorry, but registration is currently not available in your region. As a result, you will not be able to create an account at this time. We appreciate your interest and apologize for any inconvenience.");
    }
    return isAllowedCountry;
  }

  sendTokenToBackend(providerToken: string, loginType: string) {
    this.loaderService.setLoading(true);
    this.authService.sendTokenToBackend(providerToken, loginType).subscribe(async (res: LoginResponses) => {
      if (res && res.access_token) {
        localStorage.setItem('token', res.access_token);
        localStorage.setItem('refreshToken', res.refresh_token);
        localStorage.setItem('expiresIn', res.expires_in);

        var user = await this.userService.getPersonalDetails().toPromise();

        this.userService.updateUser(user);

        localStorage.setItem('ProfilePictureName', user.profilePicture);

        var img = await this.userService.getProfilePicture(user.profilePicture).toPromise();
        localStorage.setItem('ProfileUrl', img);

        this.resetForm();

        this.signUpClickEvent.emit(localStorage.getItem('ProfileUrl'));

        this.isDisableSignUpBtn = false;
        this.toastr.success('Login successful!');

        if (this.returnURL) {
          this.router.navigate([this.returnURL]);
          this.authService.updateReturnURL('');
        } else {
          this.router
            .navigateByUrl('', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['']);
            });
        }
      } else {
        this.isDisableSignUpBtn = false;
      }
      this.closeAllModals()
      this.loaderService.setLoading(false);
    });
  }

  closeAllModals() {
    this.modalService.dismissAll();
  }
}
